import Keycloak, { type KeycloakInitOptions } from 'keycloak-js'

/**
 * Extract the Keycloak base URL (everything before `/realms/`) from a full
 * OIDC authority URL such as `http://localhost:51080/kc/realms/my-realm`.
 */
function parseBaseUrl(authority: string): string {
  const match = authority.match(/^(.+)\/realms\/[^/]+$/)
  return match ? match[1] : authority
}

/**
 * Extract the realm name from a full OIDC authority URL.
 */
function parseRealm(authority: string): string {
  const match = authority.match(/\/realms\/([^/]+)$/)
  return match ? match[1] : ''
}

/** True when any DNS label is exactly `jppa`. */
function isJppaHostname(hostname: string): boolean {
  return hostname
    .toLowerCase()
    .split('.')
    .some((label) => label === 'jppa')
}

/**
 * Pick the Keycloak server URL based on the current hostname so that JPPA
 * users authenticate via auth.jppa.triploms.com, TMS users via auth.triploms.com,
 * and Proaxiv users via auth.proaxiv.com (same realm, different auth
 * hostname/issuer — see KEYCLOAK_ADDITIONAL_ISSUERS in prod.config/stg.config).
 * Which login theme each brand actually sees is controlled separately, by
 * clientId — see {@link resolveKeycloakClientId}.
 *
 * JPPA hosts are nested under triploms.com, so they must be matched before the
 * generic TMS `*.triploms.com` rule.
 */
function resolveKeycloakUrl(): string {
  const { hostname } = window.location
  if (isJppaHostname(hostname) && import.meta.env.VITE_KEYCLOAK_JPPA_URL) {
    return import.meta.env.VITE_KEYCLOAK_JPPA_URL as string
  }
  const isTms = hostname === 'triploms.com' || hostname.endsWith('.triploms.com')
  if (isTms && import.meta.env.VITE_KEYCLOAK_TMS_URL) {
    return import.meta.env.VITE_KEYCLOAK_TMS_URL as string
  }
  const authority = import.meta.env.VITE_OIDC_AUTHORITY as string | undefined
  if (authority) return parseBaseUrl(authority)
  return import.meta.env.VITE_KEYCLOAK_URL as string
}

/**
 * Resolve the realm name from VITE_OIDC_AUTHORITY (primary) or
 * VITE_KEYCLOAK_REALM (fallback).
 */
function resolveKeycloakRealm(): string {
  const authority = import.meta.env.VITE_OIDC_AUTHORITY as string | undefined
  if (authority) return parseRealm(authority)
  return import.meta.env.VITE_KEYCLOAK_REALM as string
}

/**
 * Pick the OIDC client id based on the current hostname so PROAXIV / JPPA users
 * get their brand-specific Keycloak login themes.
 *
 * The realm is shared by all brands and its default login theme is
 * "triploms" (see keycloak/realm_templates/proaxiv.json and
 * keycloak/themes/README.md). Keycloak only supports theme overrides at the
 * per-client level, so branded hostnames use dedicated clientIds that carry
 * a `login_theme` override; everything else falls through to the default
 * client id and therefore renders the realm default (triploms).
 */
function resolveKeycloakClientId(): string {
  const { hostname } = window.location
  if (isJppaHostname(hostname) && import.meta.env.VITE_KEYCLOAK_JPPA_CLIENT_ID) {
    return import.meta.env.VITE_KEYCLOAK_JPPA_CLIENT_ID as string
  }
  const isProaxiv = hostname === 'proaxiv.com' || hostname.endsWith('.proaxiv.com')
  if (isProaxiv && import.meta.env.VITE_KEYCLOAK_PROAXIV_CLIENT_ID) {
    return import.meta.env.VITE_KEYCLOAK_PROAXIV_CLIENT_ID as string
  }
  return (import.meta.env.VITE_OIDC_CLIENT_ID ?? import.meta.env.VITE_KEYCLOAK_CLIENT_ID) as string
}

/**
 * Singleton Keycloak instance, initialised once by {@link AuthProvider}.
 *
 * Config is read from Vite env vars so that each deployment target
 * (dev / staging / prod) can point to its own realm without a code change.
 */
export const keycloak = new Keycloak({
  url: resolveKeycloakUrl(),
  realm: resolveKeycloakRealm(),
  clientId: resolveKeycloakClientId(),
})

/**
 * Module-level promise so `keycloak.init()` is only ever called once,
 * even if `AuthProvider` mounts more than once (e.g. React StrictMode).
 */
let _initPromise: Promise<boolean> | undefined

/**
 * Returns (and memoises) the Keycloak initialisation promise so that
 * `keycloak.init()` is only ever called once, even if `AuthProvider` mounts
 * more than once (e.g. React StrictMode double-invoke).
 */
export function getInitPromise(options: KeycloakInitOptions): Promise<boolean> {
  if (!_initPromise) {
    _initPromise = keycloak.init(options)
  }
  return _initPromise
}
